import { useState } from 'react';
import {
	Button,
	Card,
	Col,
	DatePicker,
	Empty,
	Modal,
	Row,
	Spin,
	Statistic,
	Table,
	Tag,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { handleAPIError, formatNumber } from '../../utils/helpers';

type EarningType =
	| 'sale'
	| 'route_completion'
	| 'daily_allowance'
	| 'reactivation'
	| 'recruitment_approval'
	| 'recruitment_first_sale';

interface AgentEarnings {
	agent_id: number;
	agent_name: string;
	total: number;
	breakdown: Partial<Record<EarningType, { count: number; amount: number }>>;
}

interface EarningsResponse {
	month: string;
	agents: AgentEarnings[];
	total: number;
}

interface DetailRow {
	id: number;
	agent: string;
	type: EarningType;
	amount: string;
	earned_on: string;
	description: string;
	retailer: string | null;
	device_id: number | null;
	settled_at: string;
}

// Ordered the way the money is thought about: what they sold, then the day rate,
// then what they did to grow the base.
const TYPES: { key: EarningType; label: string }[] = [
	{ key: 'sale', label: 'Sales' },
	{ key: 'route_completion', label: 'Routes' },
	{ key: 'daily_allowance', label: 'Allowance' },
	{ key: 'reactivation', label: 'Reactivations' },
	{ key: 'recruitment_approval', label: 'Recruited' },
	{ key: 'recruitment_first_sale', label: 'First sales' },
];

export default function FieldCommissions() {
	const [month, setMonth] = useState<Dayjs>(dayjs());
	const [drill, setDrill] = useState<{
		agentId?: number;
		type?: EarningType;
		who: string;
		what: string;
	} | null>(null);

	const monthStr = month.format('YYYY-MM');

	const { data, isLoading, error } = useQuery<EarningsResponse>({
		queryKey: ['field-earnings', monthStr],
		queryFn: async () => {
			const res = await axios.get('/admin/field/earnings', {
				params: { month: monthStr },
				withCredentials: true,
			});
			return res.data.results;
		},
	});
	if (error) handleAPIError(error);

	const detail = useQuery<{ rows: DetailRow[] }>({
		queryKey: ['field-earnings-detail', monthStr, drill?.agentId, drill?.type],
		enabled: !!drill,
		queryFn: async () => {
			const res = await axios.get('/admin/field/earnings/detail', {
				params: { month: monthStr, agent_id: drill!.agentId, type: drill!.type },
				withCredentials: true,
			});
			return res.data.results;
		},
	});

	const cell = (
		value: number | undefined,
		count: number | undefined,
		agentId: number | undefined,
		type: EarningType | undefined,
		who: string,
		what: string,
	) => {
		if (!value) return <span style={{ color: '#ccc' }}>—</span>;
		return (
			<Button
				type="link"
				size="small"
				style={{ padding: 0, height: 'auto', textAlign: 'right' }}
				onClick={() => setDrill({ agentId, type, who, what })}
			>
				<span>
					{formatNumber(value)}
					{count ? (
						<span style={{ color: '#999', fontSize: 11, marginLeft: 4 }}>×{count}</span>
					) : null}
				</span>
			</Button>
		);
	};

	const columns = [
		{
			title: 'Agent',
			fixed: 'left' as const,
			width: 190,
			render: (row: AgentEarnings) => <span style={{ fontWeight: 500 }}>{row.agent_name}</span>,
		},
		...TYPES.map((t) => ({
			title: t.label,
			align: 'right' as const,
			width: 120,
			sorter: (a: AgentEarnings, b: AgentEarnings) =>
				(a.breakdown[t.key]?.amount ?? 0) - (b.breakdown[t.key]?.amount ?? 0),
			render: (row: AgentEarnings) =>
				cell(
					row.breakdown[t.key]?.amount,
					row.breakdown[t.key]?.count,
					row.agent_id,
					t.key,
					row.agent_name,
					t.label,
				),
		})),
		{
			title: 'Total',
			align: 'right' as const,
			width: 140,
			fixed: 'right' as const,
			defaultSortOrder: 'descend' as const,
			sorter: (a: AgentEarnings, b: AgentEarnings) => a.total - b.total,
			render: (row: AgentEarnings) => (
				<strong>{cell(row.total, undefined, row.agent_id, undefined, row.agent_name, 'All earnings')}</strong>
			),
		},
	];

	const detailColumns = [
		{ title: 'Date', dataIndex: 'earned_on', width: 110 },
		{
			title: 'Type',
			dataIndex: 'type',
			width: 170,
			render: (t: EarningType) => <Tag>{TYPES.find((x) => x.key === t)?.label ?? t}</Tag>,
		},
		{ title: 'Agent', dataIndex: 'agent', width: 150 },
		{ title: 'What for', dataIndex: 'description' },
		{
			title: 'Amount',
			dataIndex: 'amount',
			align: 'right' as const,
			width: 110,
			render: (a: string) => <strong>{formatNumber(Number(a))}</strong>,
		},
	];

	const detailTotal = (detail.data?.rows ?? []).reduce((s, r) => s + Number(r.amount), 0);

	return (
		<AppLayout title="Agent Commissions" subtitle="What the field team earned">
			<div style={{ padding: 16 }}>
				<Card size="small" style={{ marginBottom: 16 }}>
					<Row gutter={16} align="middle">
						<Col>
							<DatePicker
								picker="month"
								value={month}
								allowClear={false}
								onChange={(v) => v && setMonth(v)}
							/>
						</Col>
						<Col flex="auto" />
						<Col>
							<Statistic title="Agents paid" value={data?.agents.length ?? 0} />
						</Col>
						<Col>
							<Statistic
								title="Month total"
								value={data ? formatNumber(data.total) : '0'}
								prefix="UGX"
							/>
						</Col>
					</Row>
				</Card>

				{isLoading ? (
					<div style={{ padding: 48, textAlign: 'center' }}>
						<Spin />
					</div>
				) : (
					<Table
						size="small"
						rowKey="agent_id"
						columns={columns}
						dataSource={data?.agents ?? []}
						pagination={false}
						scroll={{ x: 1100 }}
						bordered
						locale={{
							emptyText: (
								<Empty description={`Nothing settled for ${month.format('MMMM YYYY')}`} />
							),
						}}
						summary={(rows) =>
							rows.length ? (
								<Table.Summary fixed>
									<Table.Summary.Row>
										<Table.Summary.Cell index={0}>
											<strong>All agents</strong>
										</Table.Summary.Cell>
										{TYPES.map((t, i) => (
											<Table.Summary.Cell key={t.key} index={i + 1} align="right">
												<strong>
													{formatNumber(
														rows.reduce((s, r) => s + (r.breakdown[t.key]?.amount ?? 0), 0),
													)}
												</strong>
											</Table.Summary.Cell>
										))}
										<Table.Summary.Cell index={TYPES.length + 1} align="right">
											<strong>{formatNumber(rows.reduce((s, r) => s + r.total, 0))}</strong>
										</Table.Summary.Cell>
									</Table.Summary.Row>
								</Table.Summary>
							) : null
						}
					/>
				)}

				<Modal
					open={!!drill}
					onCancel={() => setDrill(null)}
					footer={null}
					width={900}
					title={
						drill && (
							<span>
								{drill.what} · {drill.who}
								<span style={{ fontWeight: 400, color: '#888', marginLeft: 8 }}>
									{month.format('MMMM YYYY')}
								</span>
							</span>
						)
					}
				>
					{detail.isFetching ? (
						<div style={{ padding: 32, textAlign: 'center' }}>
							<Spin />
						</div>
					) : (
						<>
							<Table
								size="small"
								rowKey="id"
								columns={detailColumns}
								dataSource={detail.data?.rows ?? []}
								pagination={{ pageSize: 10, hideOnSinglePage: true }}
							/>
							{!!detail.data?.rows.length && (
								<div style={{ textAlign: 'right', marginTop: 8 }}>
									<strong>UGX {formatNumber(detailTotal)}</strong>
								</div>
							)}
						</>
					)}
				</Modal>
			</div>
		</AppLayout>
	);
}
